import React, { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import CustomNavbar from './CustomNavbar';
import api from './axiosSetup';
import MyDataHaes from './myDataHaes';
import SRVHaesApplies from './srvHaesApplies';
import SRVSpareParts from './srvSpareParts';
import { useApp } from '../AppContext';
import PushNotifications from './pushNotifications';
import SRVMyWorkAssignments from './workAssignment/srvMyWorkAssignments';

const fieldLabels = {
    email: "E-Mail",
    password: "Passwort",
    confirmPassword: "Passwort bestätigen",
    firstName: "Vorname",
    lastName: "Nachname",
    isBlockedUntill: "Gesperrt bis",
    birthDate: "Geburtsdatum",
    street: "Straße",
    houseNumber: "Hausnummer",
    city: "Ort",
    IBAN: "IBAN"
};

// Funktion zum Umwandeln DD.MM.YYYY → YYYY-MM-DD (für API)
const formatDateToISO = (dateString) => {
    if (!dateString || !dateString.includes(".")) return dateString;
    const [day, month, year] = dateString.split(".");
    return `${year}-${month}-${day}`;
};

const getPasswordStrength = (pw) => {
    if (!pw) return { score: 0, label: '', color: '' };
    let score = 0;
    if (pw.length >= 8)  score++;
    if (pw.length >= 12) score++;
    if (/[A-Z]/.test(pw)) score++;
    if (/[0-9]/.test(pw)) score++;
    if (/[^A-Za-z0-9]/.test(pw)) score++;
    if (score <= 1) return { score, label: 'Sehr schwach', color: '#ef4444' };
    if (score === 2) return { score, label: 'Schwach',      color: '#f97316' };
    if (score === 3) return { score, label: 'Mittel',       color: '#eab308' };
    if (score === 4) return { score, label: 'Stark',        color: '#22c55e' };
    return              { score, label: 'Sehr stark',   color: '#16a34a' };
};

const MyData = () => {
    const { isOn } = useApp();
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        confirmPassword: '',
        firstName: '',
        lastName: '',
        birthDate: '',
        isBlockedUntill: "Gesperrt bis",
        street: '',
        houseNumber: '',
        city: '',
        iban: ''
    });
    const [isEditing, setIsEditing] = useState(false);
    const [passwordError, setPasswordError] = useState('');
    const [oldPassword, setOldPassword] = useState('');
    const location = useLocation();
    const [successMessage, setSuccessMessage] = useState('');
    const [profilePicture, setProfilePicture] = useState(null);
    const [picUploading, setPicUploading] = useState(false);
    const [picError, setPicError] = useState('');
    const fileInputRef = useRef();

    useEffect(() => {

        const fetchData = async () => {
            try {
                const token = localStorage.getItem('authToken');
                const response = await api.get('/api/my-data', {
                    headers: { Authorization: `Bearer ${token}` }
                });

                // Sicherstellen, dass das Datum formatiert wird
                const formattedData = {
                    ...response.data,
                    birthDate: response.data.birthDate ? formatDateToISO(response.data.birthDate) : ""
                };

                setFormData(formattedData);
                if (response.data.profilePicture) setProfilePicture(response.data.profilePicture);
            } catch (error) {
                console.error('Fehler beim Abrufen der Benutzerdaten:', error);
            }
        };

        fetchData();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;

        setFormData({
            ...formData,
            [name]: (name === 'birthDate' || name === 'isBlockedUntill') ? formatDateToISO(value) : value
        });
    };

    const handleEdit = () => {
        setIsEditing(true);
    };

    const handleSave = async () => {
        if (formData.password !== formData.confirmPassword) {
            setPasswordError('Die Passwörter stimmen nicht überein!');
            return;
        }
        if (formData.password && !oldPassword) {
            setPasswordError('Bitte gib dein aktuelles Passwort ein, um es zu ändern.');
            return;
        }

        try {
            const token = localStorage.getItem('authToken');
            const payload = {
                ...formData,
                birthDate: formatDateToISO(formData.birthDate),
                ...(formData.password ? { oldPassword } : {})
            };
            delete payload.confirmPassword;
            await api.post('/api/edit-my-data', payload, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSuccessMessage('Deine Daten wurden erfolgreich aktualisiert.');
            setIsEditing(false);
            setPasswordError('');
            setOldPassword('');
        } catch (error) {
            const msg = error.response?.data?.message;
            if (msg) setPasswordError(msg);
            console.error('Fehler beim Speichern der Daten:', error);
        }
    };
    

    const handleProfilePicUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setPicError('');
        setPicUploading(true);
        const formData = new FormData();
        formData.append('picture', file);
        try {
            const token = localStorage.getItem('authToken');
            const res = await api.post('/api/upload-profile-picture', formData, {
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
            });
            setProfilePicture(res.data.url);
        } catch (err) {
            setPicError(err.response?.data?.message || 'Fehler beim Hochladen.');
        } finally {
            setPicUploading(false);
        }
    };

    const handleDeleteProfilePic = async () => {
        try {
            const token = localStorage.getItem('authToken');
            await api.delete('/api/delete-profile-picture', { headers: { Authorization: `Bearer ${token}` } });
            setProfilePicture(null);
        } catch (err) {
            setPicError('Fehler beim Löschen.');
        }
    };

    const handleDsgvoExport = () => {
        const token = localStorage.getItem('authToken');
        const link = document.createElement('a');
        link.href = `/api/dsgvo-export`;
        link.setAttribute('download', 'dsgvo-auskunft.pdf');
        // Fetch mit Auth-Header
        fetch('/api/dsgvo-export', { headers: { Authorization: `Bearer ${token}` } })
            .then(r => r.blob())
            .then(blob => {
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'dsgvo-auskunft.pdf';
                a.click();
                URL.revokeObjectURL(url);
            });
    };

    return (
        <div>
            <CustomNavbar />
            <div className="container mt-4">
                <h1>Meine Daten</h1>
                {location.state && location.state.message && (
                    <div className="alert alert-warning">{location.state.message}</div>
                )}

                {!isEditing && (
                    <button className="btn btn-primary mb-3" onClick={handleEdit}>Meine Daten bearbeiten</button>
                )}

                <form>
                    {Object.entries(formData).map(([key, value]) => (
                        key !== 'password' && key !== 'confirmPassword' && key !== 'ID' && (
                            <div className="mb-3" key={key}>
                                <label className="form-label">{fieldLabels[key]}</label>
                                <input
                                    type={key === 'birthDate' || key === 'isBlockedUntill' ? 'date' : 'text'}
                                    className="form-control"
                                    name={key}
                                    value={key === 'birthDate' || key === 'isBlockedUntill' ? (value || "") : value}
                                    onChange={handleChange}
                                    disabled={!isEditing || key === 'isBlockedUntill'}
                                />
                            </div>
                        )
                    ))}

                    {/* Passwortfelder am Ende */}
                    {isEditing && (
                        <>
                            <hr className="my-3" />
                            <p className="small text-secondary mb-2">Passwort ändern (optional – leer lassen um beizubehalten)</p>
                            <div className="mb-3">
                                <label className="form-label">Aktuelles Passwort</label>
                                <input
                                    type="password"
                                    className="form-control"
                                    value={oldPassword}
                                    onChange={e => setOldPassword(e.target.value)}
                                    placeholder="Aktuelles Passwort eingeben"
                                />
                            </div>
                            <div className="mb-3">
                                <label className="form-label">{fieldLabels['password']}</label>
                                <input
                                    type="password"
                                    className="form-control"
                                    name="password"
                                    value={formData.password}
                                    onChange={handleChange}
                                    placeholder="Neues Passwort (leer = keine Änderung)"
                                />
                                {formData.password && (() => {
                                    const s = getPasswordStrength(formData.password);
                                    return (
                                        <div className="mt-2">
                                            <div className="d-flex gap-1 mb-1">
                                                {[1,2,3,4,5].map(i => (
                                                    <div key={i} style={{
                                                        flex: 1, height: 4, borderRadius: 2,
                                                        background: i <= s.score ? s.color : '#dee2e6',
                                                        transition: 'background 0.2s'
                                                    }} />
                                                ))}
                                            </div>
                                            <small style={{ color: s.color, fontWeight: 600 }}>{s.label}</small>
                                        </div>
                                    );
                                })()}
                            </div>

                            <div className="mb-3">
                                <label className="form-label">{fieldLabels['confirmPassword']}</label>
                                <input
                                    type="password"
                                    className="form-control"
                                    name="confirmPassword"
                                    value={formData.confirmPassword}
                                    onChange={handleChange}
                                    placeholder="Neues Passwort bestätigen"
                                />
                            </div>

                            {passwordError && <div className="alert alert-danger">{passwordError}</div>}
                        </>
                    )}

                    {isEditing && (
                        <button type="button" className="btn btn-success mt-3" onClick={handleSave}>Speichern</button>
                    )}
                    {successMessage && <div className="alert alert-success">{successMessage}</div>}
                </form>

                {/* Profilbild */}
                <hr className="my-4"/>
                <h5>Profilbild</h5>
                <div className="d-flex align-items-center gap-3 mb-3">
                    <div
                        className="rounded-circle overflow-hidden bg-secondary d-flex align-items-center justify-content-center"
                        style={{ width: 80, height: 80, flexShrink: 0 }}
                    >
                        {profilePicture
                            ? <img src={profilePicture} alt="Profilbild" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            : <span style={{ fontSize: '2rem' }}>👤</span>
                        }
                    </div>
                    <div className="d-flex flex-column gap-2">
                        <input
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            ref={fileInputRef}
                            style={{ display: 'none' }}
                            onChange={handleProfilePicUpload}
                        />
                        <button className="btn btn-sm btn-outline-primary" onClick={() => fileInputRef.current?.click()} disabled={picUploading}>
                            {picUploading ? 'Wird hochgeladen...' : 'Bild hochladen'}
                        </button>
                        {profilePicture && (
                            <button className="btn btn-sm btn-outline-danger" onClick={handleDeleteProfilePic}>Bild entfernen</button>
                        )}
                    </div>
                </div>
                {picError && <div className="alert alert-danger py-2">{picError}</div>}

                {/* Push-Benachrichtigungen */}
                <hr className="my-4"/>
                <h5>Benachrichtigungen</h5>
                <PushNotifications />

                {/* DSGVO Datenauskunft */}
                <hr className="my-4"/>
                <h5>Datenschutz</h5>
                <p className="text-secondary small">Du hast das Recht, eine Auskunft über deine gespeicherten Daten zu erhalten (Art. 15 DSGVO).</p>
                <button className="btn btn-outline-warning btn-sm" onClick={handleDsgvoExport}>
                    📄 Meine Daten herunterladen (PDF)
                </button>

                {isOn('Modul_Arbeitseinsatz') && <>
                <hr className="my-4"/>
                <h5>Meine Arbeitseinsätze</h5>
                <SRVMyWorkAssignments />
                </>}

                {isOn('Modul_Haes') && <>
                <hr className="my-4"/>
                <MyDataHaes/>
                <br/>
                <hr/>
                <br/>
                <SRVHaesApplies/>
                <br/>
                <hr/>
                </>}
                <br/>
                {isOn('Modul_Haes') && <><SRVSpareParts /><br/><hr/><br/></>}
            </div>
        </div>
    );
};

export default MyData;
